/**
 * Admin Page
 *
 * Features:
 * - Grant and revoke PBS-level access (Admin / Project Owner)
 *
 * Access: Admin role only
 */

import React, { useState, useEffect, useCallback } from 'react';
import PermissionGuard from '../components/PermissionGuard';
import AccessDenied from '../components/AccessDenied';
import accessService, { AccessEntry } from '../services/accessService';

const AdminSalariesPage: React.FC = () => {
  // State
  const [success, setSuccess] = useState<string | null>(null);

  // PBS Access management state
  const [accessList, setAccessList] = useState<AccessEntry[]>([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [accessSaving, setAccessSaving] = useState(false);
  const [accessForm, setAccessForm] = useState({
    email: '',
    role: 'project_owner'
  });

  const extractApiError = (err: any) =>
    err?.response?.data?.error || err?.message || 'Something went wrong';

  const loadAccessList = useCallback(async () => {
    try {
      setAccessLoading(true);
      setAccessError(null);
      const data = await accessService.list();
      setAccessList(data);
    } catch (err: any) {
      setAccessError(extractApiError(err));
    } finally {
      setAccessLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccessList();
  }, [loadAccessList]);

  const handleAccessGrant = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!accessForm.email.trim()) {
      setAccessError('Email is required');
      return;
    }
    try {
      setAccessSaving(true);
      setAccessError(null);
      await accessService.grant({
        email: accessForm.email.trim(),
        role: accessForm.role
      });
      setAccessForm((prev) => ({ email: '', role: prev.role }));
      setSuccess('Access granted successfully');
      loadAccessList();
    } catch (err: any) {
      setAccessError(extractApiError(err));
    } finally {
      setAccessSaving(false);
    }
  };

  const handleAccessRemove = async (entry: AccessEntry) => {
    const confirmed = window.confirm(
      `Remove ${entry.user?.login || entry.user_id} from ${entry.role} access?`
    );
    if (!confirmed) {
      return;
    }
    try {
      setAccessError(null);
      await accessService.revoke(entry.role, entry.user_id);
      setSuccess('Access removed successfully');
      loadAccessList();
    } catch (err: any) {
      setAccessError(extractApiError(err));
    }
  };

  const formatAccessTimestamp = (value?: string) => {
    if (!value) return '—';
    return new Date(value).toLocaleString();
  };

  return (
    <PermissionGuard
      pbsRole="admin"
      fallback={
        <AccessDenied
          title="Admin Access Required"
          message="Only PBS-approved admins can manage member salaries."
        />
      }
    >
      <div className="min-h-screen bg-gradient-to-br from-white via-amber-50/40 to-orange-50/30 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          {/* <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm p-6 mb-6 border border-amber-100/50">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Member Cost Management</h1>
              </div>
            </div>
          </div> */}

          {/* PBS Access Management */}
        <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm p-6 mb-6 border border-amber-100/50">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">TPBS Access Management</h2>
              <p className="text-sm text-gray-600">
                Grant or revoke PBS-level Admin or Project Owner access without touching Redmine.
              </p>
            </div>
            <button
              onClick={loadAccessList}
              className="self-start md:self-auto px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              disabled={accessLoading}
            >
              Refresh
            </button>
          </div>

          {accessError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {accessError}
            </div>
          )}

          <form onSubmit={handleAccessGrant}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User Email/Login</label>
                <input
                  type="email"
                  value={accessForm.email}
                  onChange={(e) => setAccessForm((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="user@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={accessForm.role}
                  onChange={(e) => setAccessForm((prev) => ({ ...prev, role: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="project_owner">Project Owner</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={accessSaving}
                >
                  {accessSaving ? 'Saving...' : 'Grant Access'}
                </button>
              </div>
            </div>
          </form>

          <div className="mt-6 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Granted On
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {accessLoading ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                      Loading access list...
                    </td>
                  </tr>
                ) : accessList.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                      No PBS access has been granted yet.
                    </td>
                  </tr>
                ) : (
                  accessList.map((entry) => (
                    <tr key={`${entry.role}-${entry.user_id}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div className="font-medium">
                          {entry.user?.firstname} {entry.user?.lastname}
                        </div>
                        <div className="text-gray-500">{entry.user?.login || entry.user_id}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 capitalize">{entry.role.replace('_', ' ')}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatAccessTimestamp(entry.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleAccessRemove(entry)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

          {/* Alerts */}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
              {success}
            </div>
          )}
        </div>
      </div>
    </PermissionGuard>
  );
};

export default AdminSalariesPage;
